import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, EditableCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
import { useIsEditing } from '../utils/hooks';
import { faqStore, markTerms, matches, POPULAR, snippet, termRx, toFaqItems, type Faq } from '../utils/faq';
import { scrollToId } from '../utils/site';
import { useStore } from '../utils/store';

const SearchIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="shrink-0 fill-none stroke-[--q-ink] stroke-[1.8] [stroke-linecap:round]">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-4-4" />
  </svg>
);

/** Highlight for every <mark> under a FAQ section root, including those inside answer HTML. */
export const MARK_STYLES =
  '[&_mark]:rounded-[2px] [&_mark]:bg-[color-mix(in_srgb,var(--q-accent)_55%,transparent)] [&_mark]:[padding:0_1px] [&_mark]:text-inherit';

/** Plain text with the search terms wrapped in <mark>. */
export function Marked({ text, terms }: { text: string; terms: string[] }) {
  const rx = termRx(terms);
  if (!rx) return <>{text}</>;
  return (
    <>
      {text.split(rx).map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part))}
    </>
  );
}

const SUGGESTIONS = 3;

/** FAQ page opener: the question, the FAQs blurb, a live search over every answer and the popular topics. */
export function FaqSearch() {
  const editing = useIsEditing();
  const faqs = useCollectionBinding<Faq>('faqsearch.faqs', { collection: 'faqs' });
  const { query } = useStore(faqStore);
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  const items = toFaqItems(faqs);
  const popular = POPULAR.map(([label, rx]) => ({ label, item: items.find((it) => rx.test(it.question)) })).filter((x) => x.item);
  const q = query.trim();
  const found = q ? items.filter((it) => matches(it, q)) : [];
  const terms = markTerms(items, q);
  const open = focused && !!q;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key !== '/' || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const showAll = () => {
    faqStore.set({ category: 'all', focus: null });
    inputRef.current?.blur();
    scrollToId('faq-list');
  };

  return (
    <section
      className={`flex justify-center bg-[--q-surface] px-[--gutter] pb-14 pt-28 max-[767px]:pb-6 max-[767px]:pt-12 ${MARK_STYLES}`}
      id="faq-search"
    >
      <div
        className="flex w-full max-w-[820px] flex-col items-center gap-6 text-center max-[767px]:items-stretch max-[767px]:gap-4 max-[767px]:text-left"
        data-reveal
      >
        <RichTextContent path="faqsearch.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="faqsearch.title" as="h1" className="h1 [&_p]:m-0" />
        <RichTextContent
          path="faqsearch.lead"
          as="p"
          className="m-0 max-w-[640px] text-lg leading-7 text-muted-foreground has-[>[data-vibesite-richtext]:empty]:hidden [&_p]:m-0 max-[767px]:text-base max-[767px]:leading-[25px]"
        />
        {(items.length > 0 || editing) && (
          <form
            className={`relative w-full max-w-[760px] rounded-t-lg border-[1.5px] border-primary bg-[--q-paper] text-left [box-shadow:0_0_0_4px_color-mix(in_srgb,var(--vibe-color-primary)_14%,transparent)] focus-within:[box-shadow:0_0_0_4px_color-mix(in_srgb,var(--vibe-color-primary)_28%,transparent)] ${open ? 'rounded-b-none' : 'rounded-b-lg'}`}
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              showAll();
            }}
          >
            <div className="flex h-[61px] items-center gap-4 pl-6 pr-1.5 max-[767px]:h-[53px] max-[767px]:gap-3 max-[767px]:pl-4 max-[767px]:pr-2">
              <SearchIcon size={22} />
              <label className="sr-only" htmlFor="faq-q">Search the FAQs</label>
              <input
                ref={inputRef}
                id="faq-q"
                className="h-full min-w-0 bg-transparent p-0 text-[--q-ink] [border:0] [flex:1_1_0] [font:inherit] [outline:none] placeholder:text-muted-foreground placeholder:opacity-100 focus-visible:[outline:none] [&::-webkit-search-cancel-button]:cursor-pointer [&]:text-lg max-[767px]:[&]:text-base"
                type="search"
                autoComplete="off"
                placeholder="Search questions, topics, or keywords…"
                value={query}
                aria-controls="faq-suggest"
                aria-expanded={open}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onKeyDown={(e) => e.key === 'Escape' && inputRef.current?.blur()}
                onChange={(e) => faqStore.set({ query: e.target.value, category: 'all', focus: null })}
              />
              {q ? (
                <button
                  className="min-h-11 shrink-0 px-2 text-[13px] font-medium leading-4 text-[--q-ink] hover:text-[--q-primary-on-paper]"
                  type="button"
                  onClick={() => faqStore.set({ query: '', focus: null })}
                >
                  ✕ Clear
                </button>
              ) : (
                <kbd
                  className="inline-flex h-7 items-center rounded-md border border-[--q-border] px-2.5 font-body text-[12px] text-muted-foreground max-[767px]:hidden"
                  aria-hidden="true"
                >
                  /
                </kbd>
              )}
              <button className="btn btn-primary [&_p]:m-0 max-[767px]:[&.btn]:hidden" type="submit">
                <RichTextContent path="faqsearch.button_label" as="span" />
              </button>
            </div>
            {open && (
              <div
                className="absolute -left-[1.5px] -right-[1.5px] top-full z-[6] flex flex-col overflow-hidden rounded-b-lg border-x-[1.5px] border-b-[1.5px] border-t border-x-primary border-b-primary border-t-[--q-border] bg-[--q-paper] [box-shadow:var(--q-shadow-md)]"
                id="faq-suggest"
                onMouseDown={(e) => e.preventDefault()}
              >
                {found.slice(0, SUGGESTIONS).map((it) => (
                  <button
                    key={it.key}
                    type="button"
                    className="flex w-full flex-col gap-1 border-b border-b-[--q-paper] bg-[--q-surface] px-4 py-3.5 text-left text-[--q-ink] [transition:background-color_var(--q-motion-fast)] hover:bg-[color-mix(in_srgb,var(--q-surface)_70%,var(--q-border))] focus-visible:bg-[color-mix(in_srgb,var(--q-surface)_70%,var(--q-border))]"
                    onClick={() => {
                      faqStore.set({ category: 'all', focus: it.key });
                      inputRef.current?.blur();
                    }}
                  >
                    <span className="flex items-center justify-between gap-4">
                      <span className="text-base leading-[22px]"><Marked text={it.question} terms={terms} /></span>
                      <span className="shrink-0 text-[12px] uppercase leading-4 tracking-[--q-tracking-label] text-muted-foreground">{it.category}</span>
                    </span>
                    <span className="text-sm leading-[22px] text-muted-foreground max-[767px]:text-[13px] max-[767px]:leading-[19px]">
                      <Marked text={snippet(it, terms)} terms={terms} />
                    </span>
                  </button>
                ))}
                <button
                  className="flex min-h-12 items-center border-t border-t-[--q-border] px-4 text-left text-sm font-medium leading-5 text-[--q-primary-on-paper] hover:underline hover:underline-offset-[3px]"
                  type="submit"
                >
                  {found.length ? `See ${found.length} ${found.length === 1 ? 'answer' : 'answers'} for “${q}” ↵` : `No answers mention “${q}” yet`}
                </button>
              </div>
            )}
          </form>
        )}
        <EditableCollectionBinding bindingPath="faqsearch.faqs" label="FAQs">
          {popular.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-sm leading-5 max-[767px]:justify-start max-[767px]:gap-x-3.5">
              <RichTextContent path="faqsearch.popular_label" as="span" className="text-muted-foreground [&_p]:m-0" />
              {popular.map(({ label, item }) => (
                <button
                  key={label}
                  type="button"
                  className="font-medium text-[--q-primary-on-paper] underline decoration-1 underline-offset-[3px] hover:decoration-2 max-[767px]:min-h-8"
                  onClick={() => faqStore.set({ query: '', category: 'all', focus: item!.key })}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </EditableCollectionBinding>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqSearch',
  displayName: 'FAQ Search',
  tokens: {
    'faqsearch.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>FAQs · <span data-type="field-token" data-field="property_name"></span></p>' },
    'faqsearch.title': { label: 'Title', type: 'richtext', defaultValue: '<p>What can we help you find?</p>' },
    'faqsearch.lead': {
      label: 'Lead',
      description: 'Mapped to the FAQs Blurb field. Hidden when empty.',
      type: 'richtext',
      defaultField: 'property_faqs_blurb',
      defaultValue: '',
      suggestedFields: ['property_faqs_blurb'],
    },
    'faqsearch.button_label': { label: 'Search Button Text', type: 'richtext', defaultValue: '<p>Search</p>' },
    'faqsearch.popular_label': { label: 'Popular Label', type: 'richtext', defaultValue: '<p>Popular:</p>' },
  },
  collectionBindings: {
    'faqsearch.faqs': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FaqSearch as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqSearch;
